import { useEffect, useState, useCallback, useMemo, useContext } from "react";
import { Box, Pagination, Skeleton } from "@mui/material";
import { TopBarContext } from "../../providers/TopBarProvider";
import { useNavigate } from "react-router-dom";
import UserItem from "../molecules/UserItem";
import UserService from "../../services/UserService";
import { ToastContext } from "../../providers/ToastProvider";
import ResetModal from "../molecules/ResetModal";
import CustomNoRows from "../atoms/CustomNoRows";

const Users = () => {
  const [resetId, setResetId] = useState(-1);
  const [reset, setReset] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [mySearch, setMySearch] = useState("");
  const { setTitle, setSearchOptions, setBtnPrimary } =
    useContext(TopBarContext);
  const { setShowToast, setToastMessage } = useContext(ToastContext);
  const userService = useMemo(() => new UserService(), []);
  const navigate = useNavigate();
  const rowsPerPage = 25;

  const getAll = useCallback(
    async (skipCount = 0, search) => {
      try {
        setIsLoading(true);
        setUsers([]);
        const res = await userService.getAll(
          rowsPerPage,
          skipCount * rowsPerPage,
          search
        );
        const totalCount = res.data.result.totalCount;
        const items = res.data.result.items;

        if (items.length === 0) {
          return;
        }

        setCount(Math.ceil(totalCount / rowsPerPage));
        setUsers([...items]);
      } catch (error) {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [userService, setUsers]
  );

  const deleteUser = (id, username, idx) => {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await userService.delete(id);

        if (!res.data.success) {
          return;
        }

        setTimeout(() => {
          setUsers((oldUsers) => {
            oldUsers.splice(idx, 1);
            return [...oldUsers];
          });
        }, 1500);

        resolve();
      } catch {
        setToastMessage([
          {
            severity: "error",
            primary: `Error al intentar eliminar el usuario ${username}`,
          },
        ]);
        setShowToast(true);
        reject();
      }
    });
  };

  const editUser = (user) =>
    navigate(`/users/edit-user?id=${user.id}`, { state: { ...user } });

  const onPagination = (_, value) => {
    setUsers([]);
    setPage(0);
    getAll(value - 1, mySearch);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    setTitle("Usuarios");
    setSearchOptions({
      label: "Buscar por usuario",
      action: (search) => {
        setMySearch(search);
        getAll(0, search);
      },
    });
    setBtnPrimary({
      label: "Agregar Usuario",
      action: () => navigate("/users/new-user"),
      icon: "plus",
    });
  }, [setTitle, setSearchOptions, setBtnPrimary, getAll, navigate]);

  useEffect(() => {
    getAll();
  }, [getAll]);

  const classes = {
    root: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, 335px)",
      gridGap: 20,
      justifyContent: "center",
      my: 8,
    },
    skeleton: {
      width: "100%",
      height: 311,
    },
    customNoRows: {
      gridColumn: "6 span",
    },
    pagination: {
      width: "fit-content",
      margin: "0 auto",
    },
  };

  return (
    <>
      <Box sx={classes.root}>
        {isLoading ? (
          new Array(rowsPerPage)
            .fill(1)
            .map((_, idx) => (
              <Skeleton
                animation="wave"
                key={`skeleton-${idx}`}
                variant="rounded"
                sx={classes.skeleton}
              />
            ))
        ) : users.length === 0 ? (
          <Box sx={classes.customNoRows}>
            <CustomNoRows
              message={
                error
                  ? "Error intentando obtener los usuarios"
                  : "No hay usuarios registrados"
              }
            />
          </Box>
        ) : (
          users.map((user, idx) => (
            <UserItem
              key={`user-${user.id}`}
              id={user.id}
              username={user.userName}
              email={user.emailAddress}
              isActive={user.isActive}
              nRoles={user.roleNames.length}
              identification={user.identification}
              names={user.fullName}
              phone={user.phoneNumber}
              handleDelete={() => deleteUser(user.id, user.userName, idx)}
              handleEdit={() => editUser(user)}
              handleReset={() => setResetId(user.id)}
              reset={reset}
            />
          ))
        )}
      </Box>
      {count > 1 && (
        <Pagination
          count={count}
          defaultPage={page}
          sx={classes.pagination}
          color="primary"
          onChange={onPagination}
        />
      )}
      <ResetModal
        userId={resetId}
        userName={users.find((u) => u.id === resetId)?.userName}
        handleClose={() => setResetId(-1)}
        handleLoading={(id) => {
          if (id > -1) {
            setReset((oldReset) =>
              oldReset.includes(id)
                ? [...oldReset.filter((r) => r !== id)]
                : [...oldReset, id]
            );
          }
        }}
        service={userService}
      />
    </>
  );
};

export default Users;
